import { ChangeEvent, useState } from 'react'
import styled from 'styled-components'
import uniqueId from 'lodash/uniqueId'
import { useField, useFormikContext } from 'formik'
import { RadioGroup as FarewillRadioGroup } from '@farewill/ui'
import { COLOR, FONT } from '@farewill/ui/tokens'

import Error from 'components/form/error'
import { FloatingWrapper } from 'components/styled/helpers/floating'
import { getFieldState } from 'components/form/helpers'

const StyledRadioGroup = styled(FarewillRadioGroup)`
  legend {
    color: ${COLOR.GREY.DARK};
    font-weight: ${FONT.WEIGHT.MEDIUM};
  }
`

type Props = {
  label: string
  name: string
  options: { label: string; value: string }[]
  handleSave?: (event: { name: string; value: string }) => void
  inline?: boolean
}

// radios with the same HTML name form one group across the whole page,
// so every card needs its own input name while still saving to `name`
const RadioGroup = ({ label, name, options, handleSave, inline }: Props) => {
  const formik = useFormikContext()
  const [{ value }, meta] = useField(name)
  const [inputName] = useState(() => uniqueId(`${name}-`))
  const { hasHighlight } = getFieldState({ formik, name })
  const hasError = !!meta.error && meta.touched

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(name, event.target.value)
    handleSave?.({ name, value: event.target.value })
  }

  return (
    <FloatingWrapper highlight={hasHighlight}>
      <StyledRadioGroup
        name={inputName}
        legend={label}
        options={options.map((option) => ({ error: hasError, ...option }))}
        horizontal={inline}
        onChange={handleChange}
        value={value}
      />
      <Error name={name} />
    </FloatingWrapper>
  )
}

export default RadioGroup
